/**
 * Navigate the browser's top frame out of the embedded app.
 *
 * Shopify's plan-selection page lives outside the app's iframe, so reaching
 * it means leaving that iframe. The obvious way to do that does not work:
 *
 *   > because embedded apps are rendered in the Shopify admin inside an
 *   > iframe, they don't have permission to manipulate the parent browser
 *   > window, including redirects
 *   — shopify.dev/docs/apps/launch/billing/shopify-app-pricing/redirect-plan-selection-page
 *
 * Writing `window.top.location.href` from a cross-origin iframe is refused by
 * the browser with a SecurityError unless the frame has user activation, and
 * it is refused silently from the app's point of view — the merchant sees a
 * button that does nothing.
 *
 * The supported mechanism is the App Bridge Navigation API, which documents
 * `open()` for "programmatic navigation ... for cases where anchor elements
 * aren't suitable" and `target="_top"` for destinations outside the iframe
 * (shopify.dev/docs/api/app-home/v1.0/apis/user-interface-and-interactions/navigation-api,
 * and "The target: "_top" option is required for apps with a landing page in
 * App Home because the plan selection page is outside the app's iframe" in
 * the Build a Billing Event guide). App Bridge — loaded from the CDN in
 * public/index.html, the same instance shopifyApi.js takes session tokens
 * from — intercepts that call and asks Shopify Admin to perform the
 * navigation, which is allowed because Admin owns the top frame.
 *
 * Outside Shopify Admin (the standalone app, tests, a dev server) there is no
 * App Bridge and no iframe to escape, so a plain same-window assignment is
 * both correct and sufficient.
 *
 * @param {string} url Absolute destination URL.
 * @returns {boolean} Whether a navigation was actually initiated.
 */
export function redirectTopFrame(url) {
  if (!url) return false;

  // Inside Shopify Admin: hand it to App Bridge.
  if (typeof window !== 'undefined' && window.shopify) {
    try {
      window.open(url, '_top');
      return true;
    } catch (_) {
      // Fall through: a refusal here must not leave the caller believing the
      // merchant is on their way somewhere.
    }
  }

  // Standalone: no iframe, no App Bridge, nothing to negotiate.
  try {
    window.location.assign(url);
    return true;
  } catch (_) {
    return false;
  }
}

export default redirectTopFrame;
